import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { withEventScope } from '@repo/event-processor';
import { Button, primarySmallTheme } from '@repo/ui/components/Buttons';
import { fireToast } from '@repo/ui/components/Toast';
import { ProfileImage } from '@repo/ui/deprecated/ProfileImage';

import Api from 'global/commentsApi';
import userApi from 'global/userApi';

import { default as CommentInputWithMentions } from './CommentInput/CommentInput';
import {
  CommentBoxContainer,
  CommentBoxWrapper,
  DummyTextArea,
  InputContainer,
  InputWrapper,
  ProfileImageContainer,
  TreeLine,
} from './styles';
import { CommentBoxProps, CommentStatus, Item, Mention } from './types';
import { convert, mergeUnmatchedMentions, trim } from './utils';

const ENTER_CODE = 'Enter';

const CommentBox: React.FC<CommentBoxProps> = ({
  postId,
  commentId,
  onNewComment,
  small = false,
  modalRef,
  getUserBySlug,
}) => {
  const user = useUserStore(userStoreSelector.user);

  const [status, setStatus] = useState<CommentStatus>('idle');
  const [text, setText] = useState<string>('');
  const [items, setItems] = useState<Item[]>([]);
  const [height, setHeight] = useState('30px');
  const [canSend, setCanSend] = useState<boolean>(true);

  const dummyRef = useRef<HTMLTextAreaElement>(null);

  const handleTextChange = (newValue: string): void => {
    setText(newValue);
    // we should wait until CommentInput recalculate items asynchronously to send them
    setCanSend(false);
  };

  const handleItemsChange = useCallback((items: Item[]): void => {
    setItems(items);
    setCanSend(true);
  }, []);

  const getConvertedUser = useCallback(
    async (slug: string) => {
      const user = await getUserBySlug(slug);
      return convert(user);
    },
    [getUserBySlug]
  );

  const handleNewComment = useCallback(async (): Promise<void> => {
    if (status === 'processing') {
      return;
    }
    if (!canSend) {
      setStatus('pending');
      return;
    }

    setStatus('processing');

    const trimmedValue = text.trim();

    if (!trimmedValue) {
      setText('');
      setStatus('idle');
      return;
    }

    const trimmedItems = trim(items);
    const mergedItems = mergeUnmatchedMentions(trimmedItems);
    const content = mergedItems.map((item) =>
      item.type === 'text'
        ? { text: item.value }
        : { text: item.value, mentionUserSlug: item.mention!.slug }
    );

    const response = await Api.postComment({
      postId,
      commentId,
      content,
    });

    if (response.error !== null) {
      fireToast({ label: 'Failed to send message', error: true });
      setStatus('rejected');
      return;
    }

    setText('');
    setStatus('resolved');

    onNewComment && onNewComment(response.data);
  }, [canSend, commentId, items, onNewComment, postId, status, text]);

  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ): Promise<void> => {
    if (event.key === ENTER_CODE && !event.shiftKey) {
      event.preventDefault();
      await handleNewComment();
    }
  };

  useEffect(() => {
    if (status === 'pending' && canSend) {
      void handleNewComment();
    }
  }, [status, canSend, handleNewComment]);

  useLayoutEffect(() => {
    if (dummyRef.current) {
      dummyRef.current.value = text;
      setHeight(dummyRef.current.scrollHeight + 'px');
    }
  }, [text]);

  const isMention = (m: Mention | null): m is Mention => m !== null;

  const handleSearch = useCallback(
    async (query: string): Promise<Mention[]> => {
      if (!query) {
        return [];
      }
      const response = await userApi.findUser(query, true);
      if ('error' in response) {
        return [];
      }
      return response.results.map(convert).filter(isMention);
    },
    []
  );

  return (
    <CommentBoxWrapper small={small}>
      {small && <TreeLine height={height} />}
      <CommentBoxContainer small={small}>
        <ProfileImageContainer>
          <ProfileImage
            profile={{
              name: user?.name ?? '',
              profileImage: user?.profileImage,
              isTeam: user?.isTeam,
            }}
            size="30px"
          />
        </ProfileImageContainer>
        <InputWrapper>
          <InputContainer>
            <DummyTextArea ref={dummyRef} rows={1} />
            <CommentInputWithMentions
              disabled={status === 'pending' || status === 'processing'}
              getUserMention={getConvertedUser}
              placeholder={small ? 'Write a reply' : 'Write a comment'}
              style={{ height }}
              value={text}
              wrapperRef={modalRef}
              onChange={handleTextChange}
              onItemsChange={handleItemsChange}
              onKeyDown={handleKeyDown}
              onSearch={handleSearch}
            />
          </InputContainer>
          <Button
            dataTestId="design-post--comments-input--post-button"
            disabled={!text}
            height="28px"
            label="Post"
            name="postCommentButton"
            theme={primarySmallTheme}
            width="60px"
            onClick={handleNewComment}
          />
        </InputWrapper>
      </CommentBoxContainer>
    </CommentBoxWrapper>
  );
};

export default withEventScope(CommentBox, { module: 'commentBox' });
